import { TFunction } from 'i18next';
import { ApiError } from '@pxtra-client-lib-legacy/gen';

const DJANGO_EXCEPTION_PATTERN = /=====> (.*)/;

const extractErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError) || !error.body) return undefined;
  return error.body.code;
};

/**
 * Extracts the raw backend validation message from an ApiError, supporting both
 * shapes a DRF endpoint can return: a field-keyed object (values joined) or a
 * Django exception string prefixed with "=====>". Returns undefined when no
 * usable message can be derived. Use this when you want to surface the backend's
 * own message rather than mapping an error code to a translation.
 */
export const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError) || !error.body) return undefined;

  const body: unknown = error.body;
  if (typeof body === 'string') {
    return DJANGO_EXCEPTION_PATTERN.exec(body)?.[1].split('\n')[0];
  }
  if (body && typeof body === 'object') {
    return Object.values(body).join(', ');
  }
  return undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  t: TFunction,
  namespace: string
): string | undefined => {
  const code = extractErrorCode(error);
  if (!code) return undefined;
  const key = `${namespace}:apiError:${code}`;
  const translated = t(key);
  return translated !== key ? translated : undefined;
};

export const getEmployeeInvitationApiErrorMessage = (
  error: unknown,
  t: TFunction
): string | undefined => getApiErrorMessage(error, t, 'employee');

export const getBonusErrorMessage = (
  error: unknown,
  t: TFunction,
  isEditing?: string | null
): string => {
  const apiMessage = getApiErrorMessage(error, t, 'bonus');
  if (apiMessage) return apiMessage;
  return isEditing ? t('common:savingError') : t('bonus:createError');
};
